/**
 * Community Permissions Utility
 * Handles permission checking for enhanced community access control
 */

/**
 * Check if user is a member of the community
 * @param {Object} community - Community object
 * @param {Object} userMembership - User membership object
 * @returns {boolean}
 */
export const isUserMember = (community, userMembership) => {
  return userMembership && userMembership.role;
};

/**
 * Check if user is a moderator or creator
 * @param {Object} userMembership - User membership object
 * @returns {boolean}
 */
export const isUserModerator = (userMembership) => {
  return (
    userMembership &&
    (userMembership.role === "creator" || userMembership.role === "moderator")
  );
};

/**
 * Check if user is the creator
 * @param {Object} userMembership - User membership object
 * @returns {boolean}
 */
export const isUserCreator = (userMembership) => {
  return userMembership && userMembership.role === "creator";
};

/**
 * Check if user has read-only access to community
 * @param {Object} community - Community object
 * @param {Object} userMembership - User membership object
 * @returns {boolean}
 */
export const hasReadOnlyAccess = (community, userMembership) => {
  // Return false if community is null/undefined
  if (!community) {
    return false;
  }

  // If user is a member, they don't have read-only access (they have full access)
  if (isUserMember(community, userMembership)) {
    return false;
  }

  // Only public communities can have read-only access
  if (community.type !== "public") {
    return false;
  }

  // Check if any public viewing setting is enabled
  const settings = community.settings || {};
  return (
    settings.allowPublicChatViewing ||
    settings.allowPublicDiscussionViewing ||
    settings.allowPublicMemberViewing
  );
};

/**
 * Check if user can view chat messages
 * @param {Object} community - Community object
 * @param {Object} userMembership - User membership object
 * @returns {Object} - { canView: boolean, canSend: boolean, isViewOnly: boolean }
 */
export const getChatPermissions = (community, userMembership) => {
  // Return default permissions if community is null/undefined
  if (!community) {
    return {
      canView: false,
      canSend: false,
      isViewOnly: false,
      isMember: false,
    };
  }

  // Members have full access
  if (isUserMember(community, userMembership)) {
    return {
      canView: true,
      canSend: true,
      isViewOnly: false,
      isMember: true,
    };
  }

  // Non-members check public viewing settings
  const canView =
    community.type === "public" && community.settings?.allowPublicChatViewing;

  return {
    canView,
    canSend: false,
    isViewOnly: canView,
    isMember: false,
  };
};

/**
 * Check if user can view and participate in discussions
 * @param {Object} community - Community object
 * @param {Object} userMembership - User membership object
 * @returns {Object} - Permissions object
 */
export const getDiscussionPermissions = (community, userMembership) => {
  // Return default permissions if community is null/undefined
  if (!community) {
    return {
      canView: false,
      canCreateThreads: false,
      canReplyToThreads: false,
      isViewOnly: false,
      isMember: false,
    };
  }

  // Members have full access
  if (isUserMember(community, userMembership)) {
    return {
      canView: true,
      canCreateThreads: true,
      canReplyToThreads: true,
      isViewOnly: false,
      isMember: true,
    };
  }

  // Non-members check public viewing settings
  const canView =
    community.type === "public" &&
    community.settings?.allowPublicDiscussionViewing;

  return {
    canView,
    canCreateThreads: false,
    canReplyToThreads: false,
    isViewOnly: canView,
    isMember: false,
  };
};

/**
 * Check if user can view member list
 * @param {Object} community - Community object
 * @param {Object} userMembership - User membership object
 * @returns {Object} - Permissions object
 */
export const getMemberViewPermissions = (community, userMembership) => {
  // Return default permissions if community is null/undefined
  if (!community) {
    return {
      canView: false,
      canViewFullDetails: false,
      isMember: false,
    };
  }

  // Members have full access
  if (isUserMember(community, userMembership)) {
    return {
      canView: true,
      canViewFullDetails: true,
      isMember: true,
    };
  }

  // Non-members check public viewing settings
  const canView =
    community.type === "public" && community.settings?.allowPublicMemberViewing;

  return {
    canView,
    canViewFullDetails: false, // Limited info only
    isMember: false,
  };
};

/**
 * Check if user can join the community directly
 * @param {Object} community - Community object
 * @param {Object} userMembership - User membership object
 * @returns {boolean}
 */
export const canDirectJoin = (community, userMembership) => {
  // Return false if community is null/undefined
  if (!community) {
    return false;
  }

  // Already a member
  if (isUserMember(community, userMembership)) {
    return false;
  }

  // Only public communities can have direct join
  return community.type === "public" && community.settings?.allowDirectJoin;
};

/**
 * Get the join method for a community
 * @param {Object} community - Community object
 * @param {Object} userMembership - User membership object
 * @returns {string} - "member" | "direct" | "request" | "private"
 */
export const getJoinMethod = (community, userMembership) => {
  // Return default if community is null/undefined
  if (!community) {
    return "request";
  }

  // Already a member
  if (isUserMember(community, userMembership)) {
    return "member";
  }

  // Private community
  if (community.type === "private") {
    return "private";
  }

  // Public community with direct join enabled
  if (community.settings?.allowDirectJoin) {
    return "direct";
  }

  // Regular join request
  return "request";
};

/**
 * Get appropriate action text for join button
 * @param {Object} community - Community object
 * @param {Object} userMembership - User membership object
 * @returns {string}
 */
export const getJoinActionText = (community, userMembership) => {
  if (!community) {
    return "Join";
  }

  const method = getJoinMethod(community, userMembership);

  switch (method) {
    case "member":
      return "View Community";
    case "direct":
      return "Join Now";
    case "request":
      return "Request to Join";
    case "private":
      return "Request to Join";
    default:
      return "Join";
  }
};

/**
 * Check if community has any public viewing enabled
 * @param {Object} community - Community object
 * @returns {boolean}
 */
export const hasPublicViewing = (community) => {
  if (!community || community.type !== "public") {
    return false;
  }

  const settings = community.settings || {};
  return (
    settings.allowPublicViewing ||
    settings.allowPublicChatViewing ||
    settings.allowPublicDiscussionViewing ||
    settings.allowPublicMemberViewing
  );
};

/**
 * Get read-only banner message
 * @param {Object} community - Community object
 * @param {Object} userMembership - User membership object
 * @returns {string|null}
 */
export const getReadOnlyMessage = (community, userMembership) => {
  if (!community || isUserMember(community, userMembership)) {
    return null;
  }

  if (hasReadOnlyAccess(community, userMembership)) {
    return "You're viewing this community in read-only mode. Join to participate!";
  }

  return null;
};

/**
 * Check if user should be redirected to preview (no access to dashboard)
 * @param {Object} community - Community object
 * @param {Object} userMembership - User membership object
 * @returns {boolean}
 */
export const shouldRedirectToPreview = (community, userMembership) => {
  // Redirect to preview if community is null/undefined
  if (!community) {
    return true;
  }

  // Members can access dashboard
  if (isUserMember(community, userMembership)) {
    return false;
  }

  // Private communities always redirect to preview for non-members
  if (community.type === "private") {
    return true;
  }

  // Public communities redirect to preview if no public viewing is enabled
  return !hasPublicViewing(community);
};
